import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaBell,
  FaCloudSun,
  FaExclamationTriangle,
  FaMicrochip,
  FaMoon,
  FaPlus,
  FaSearch,
  FaSun,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { useMunicipiosAutocomplete } from '../hooks/useMunicipios';
import { variaveisDisponiveis } from '../constants/variaveis';
import { AlertasService } from '../services/AlertasService';
import { supabase } from '../supabaseClient';
import { useToast } from './Toast';
import './UnifiedAlertsCenter.css';

const SOURCE_OPTIONS = [
  { key: 'all', label: 'Todos' },
  { key: 'previsao', label: 'Previsão', icon: FaCloudSun },
  { key: 'sensor', label: 'Sensores', icon: FaMicrochip },
];

const SENSOR_TEMPLATES = {
  meteorologico: [
    { id: 'geada', label: 'Risco de geada', variable: 'temp_c', condition: 'menor que', value: 4, description: 'Temperatura abaixo de 4°C' },
    { id: 'calor', label: 'Calor excessivo', variable: 'temp_c', condition: 'maior que', value: 35, description: 'Temperatura acima de 35°C' },
    { id: 'chuva', label: 'Chuva intensa', variable: 'rain_mm', condition: 'maior que', value: 25, description: 'Chuva acima de 25 mm' },
  ],
  umidade_solo: [
    { id: 'solo_seco', label: 'Solo seco', variable: 'umidade_solo_perc', condition: 'menor que', value: 20, description: 'Umidade abaixo de 20%' },
    { id: 'solo_encharcado', label: 'Solo encharcado', variable: 'umidade_solo_perc', condition: 'maior que', value: 80, description: 'Umidade acima de 80%' },
  ],
  chuva_presenca: [
    { id: 'chuva_detectada', label: 'Molhado detectado', variable: 'molhado', condition: 'igual a', value: 1, description: 'Chuva ou irrigação detectada' },
    { id: 'ficou_seco', label: 'Sensor seco', variable: 'molhado', condition: 'igual a', value: 0, description: 'Fim da presença de água' },
  ],
};

const SENSOR_VARIABLE_LABELS = {
  temp_c: 'Temperatura',
  rain_mm: 'Chuva',
  rh_perc: 'Umidade relativa',
  umidade_solo_perc: 'Umidade do solo',
  temp_solo: 'Temperatura do solo',
  molhado: 'Estado seco/molhado',
};

function normalizeSource(alert) {
  return alert?.fonte === 'sensor' || alert?.device_id ? 'sensor' : 'previsao';
}

function conditionLabel(condition) {
  return {
    'maior que': 'acima de',
    'menor que': 'abaixo de',
    'igual a': 'igual a',
  }[condition] || condition;
}

function alertVariableLabel(alert) {
  if (normalizeSource(alert) === 'sensor') {
    return SENSOR_VARIABLE_LABELS[alert.variavel] || alert.variavel || 'Leitura do sensor';
  }
  return variaveisDisponiveis.find(item => item.value === alert.variavel)?.label || alert.variavel || 'Condição climática';
}

function sourceMeta(source) {
  return source === 'sensor'
    ? { label: 'Sensor de campo', icon: FaMicrochip, target: 'sensores' }
    : { label: 'Previsão climática', icon: FaCloudSun, target: 'previsao' };
}

export default function UnifiedAlertsCenter({ userId, darkMode, setDarkMode, onNavigate, previewMode = false, previewData = null }) {
  const toast = useToast();
  const [alerts, setAlerts] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [formSource, setFormSource] = useState('previsao');
  const [saving, setSaving] = useState(false);
  const [municipio, setMunicipio] = useState('');
  const [searchMunicipio, setSearchMunicipio] = useState('');
  const [showMunicipioDropdown, setShowMunicipioDropdown] = useState(false);
  const [variable, setVariable] = useState('');
  const [condition, setCondition] = useState('');
  const [value, setValue] = useState('');
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { municipios, loading: loadingMunicipios } = useMunicipiosAutocomplete(searchMunicipio);

  useEffect(() => {
    let active = true;
    async function loadRules() {
      if (previewMode) {
        setAlerts(previewData?.alerts || []);
        setSensors(previewData?.sensors || []);
        setLoadError('');
        setLoading(false);
        return;
      }
      if (!userId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError('');
      const [alertsResponse, sensorsResponse] = await Promise.all([
        supabase.from('alertas').select('*').eq('user_id', userId).order('criado_em', { ascending: false }),
        supabase.from('sensores_usuarios').select('*').eq('user_id', userId).order('nome_sensor', { ascending: true }),
      ]);
      if (!active) return;
      if (alertsResponse.error) {
        setLoadError('Não foi possível carregar as regras de alerta.');
      } else {
        setAlerts(alertsResponse.data || []);
      }
      if (!sensorsResponse.error) setSensors(sensorsResponse.data || []);
      setLoading(false);
    }
    loadRules();
    return () => { active = false; };
  }, [previewData, previewMode, userId]);

  useEffect(() => {
    function closeDropdown(event) {
      if (!event.target.closest('.unified-alerts-municipio')) setShowMunicipioDropdown(false);
    }
    if (!showMunicipioDropdown) return undefined;
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, [showMunicipioDropdown]);

  const counts = useMemo(() => ({
    all: alerts.length,
    previsao: alerts.filter(alert => normalizeSource(alert) === 'previsao').length,
    sensor: alerts.filter(alert => normalizeSource(alert) === 'sensor').length,
  }), [alerts]);

  const visibleAlerts = useMemo(() => activeSource === 'all'
    ? alerts
    : alerts.filter(alert => normalizeSource(alert) === activeSource), [activeSource, alerts]);

  const selectedSensor = sensors.find(sensor => String(sensor.id) === selectedSensorId);
  const templates = SENSOR_TEMPLATES[selectedSensor?.tipo_sensor || 'meteorologico'] || SENSOR_TEMPLATES.meteorologico;
  const selectedTemplate = templates.find(template => template.id === selectedTemplateId);

  function resetForm() {
    setMunicipio('');
    setSearchMunicipio('');
    setVariable('');
    setCondition('');
    setValue('');
    setSelectedSensorId('');
    setSelectedTemplateId('');
    setShowMunicipioDropdown(false);
  }

  function openForm(source = activeSource === 'sensor' ? 'sensor' : 'previsao') {
    resetForm();
    setFormSource(source);
    setShowForm(true);
  }

  async function saveAlert(event) {
    event.preventDefault();
    if (saving) return;
    let payload;
    if (formSource === 'sensor') {
      if (!selectedSensor || !selectedTemplate) {
        toast.warning('Selecione o sensor e o tipo de alerta.');
        return;
      }
      payload = {
        user_id: userId,
        fonte: 'sensor',
        device_id: selectedSensor.device_id,
        municipio: selectedSensor.nome_sensor || selectedSensor.device_id,
        variavel: selectedTemplate.variable,
        condicao: selectedTemplate.condition,
        valor: selectedTemplate.value,
        criado_em: new Date().toISOString(),
      };
    } else {
      if (!municipio || !variable || !condition || value === '' || Number.isNaN(Number(value))) {
        toast.warning('Preencha todos os campos do alerta climático.');
        return;
      }
      payload = {
        user_id: userId,
        fonte: 'previsao',
        municipio,
        variavel: variable,
        condicao: condition,
        valor: Number(value),
        criado_em: new Date().toISOString(),
      };
    }

    setSaving(true);
    if (previewMode) {
      const localAlert = { ...payload, id: `preview-${Date.now()}` };
      setAlerts(current => [localAlert, ...current]);
      setShowForm(false);
      resetForm();
      setActiveSource(formSource);
      toast.success('Regra adicionada à simulação local.');
      setSaving(false);
      return;
    }
    const { data, error } = await supabase.from('alertas').insert([payload]).select();
    if (error || !data?.length) {
      toast.error(`Erro ao criar alerta: ${error?.message || 'tente novamente'}`);
      setSaving(false);
      return;
    }
    setAlerts(current => [data[0], ...current]);
    setShowForm(false);
    resetForm();
    setActiveSource(formSource);
    toast.success('Regra de alerta criada na Central.');
    if (formSource === 'previsao') AlertasService.verificarAlertasUsuario(userId);
    setSaving(false);
  }

  async function removeAlert(alert) {
    if (!window.confirm('Remover esta regra de alerta?')) return;
    if (previewMode) {
      setAlerts(current => current.filter(item => item.id !== alert.id));
      toast.success('Regra removida da simulação local.');
      return;
    }
    const { error } = await supabase.from('alertas').delete().eq('id', alert.id).eq('user_id', userId);
    if (error) {
      toast.error(`Erro ao remover alerta: ${error.message}`);
      return;
    }
    setAlerts(current => current.filter(item => item.id !== alert.id));
    toast.success('Regra de alerta removida.');
  }

  return (
    <div className={`unified-alerts-shell ${darkMode ? 'is-dark' : ''}`}>
      <header className="unified-alerts-topbar">
        <button type="button" onClick={() => onNavigate('dashboard')} aria-label="Voltar ao painel"><FaArrowLeft /></button>
        <div className="unified-alerts-brand">
          <span><FaBell /></span>
          <div><strong>Central de Alertas</strong><small>Regras de previsão e sensores em um só lugar</small></div>
        </div>
        <button type="button" onClick={() => setDarkMode(value => !value)} aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}>
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
      </header>

      <main className="unified-alerts-main">
        <section className="unified-alerts-hero">
          <div>
            <span>Monitoramento centralizado</span>
            <h1>Todos os alertas configurados em uma única central</h1>
            <p>Crie e acompanhe regras baseadas na previsão do tempo ou nas leituras dos sensores, sempre identificando a origem e o contexto monitorado.</p>
          </div>
          <div className="unified-alerts-summary" aria-label="Resumo das regras de alerta">
            <div><small>Total</small><strong>{counts.all}</strong><span>regras ativas</span></div>
            <div><small>Previsão</small><strong>{counts.previsao}</strong><span>por município</span></div>
            <div><small>Sensores</small><strong>{counts.sensor}</strong><span>dados de campo</span></div>
          </div>
        </section>

        <section className="unified-alerts-panel">
          <div className="unified-alerts-panel-heading">
            <div><span>Regras configuradas</span><h2>O que está sendo monitorado</h2></div>
            <button type="button" className="unified-alerts-new" onClick={() => openForm()}><FaPlus /> Novo alerta</button>
          </div>

          <div className="unified-alerts-source-filters" aria-label="Filtrar regras por origem">
            {SOURCE_OPTIONS.map(option => (
              <button
                type="button"
                key={option.key}
                className={activeSource === option.key ? 'is-active' : ''}
                aria-pressed={activeSource === option.key}
                onClick={() => setActiveSource(option.key)}
              >
                {option.icon && <option.icon />} {option.label} <strong>{counts[option.key]}</strong>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="unified-alerts-state"><span className="unified-alerts-spinner" />Carregando regras...</div>
          ) : loadError ? (
            <div className="unified-alerts-state is-error"><FaExclamationTriangle /><strong>{loadError}</strong><span>Atualize a página para tentar novamente.</span></div>
          ) : visibleAlerts.length === 0 ? (
            <div className="unified-alerts-state">
              <FaBell />
              <strong>Nenhuma regra nesta origem</strong>
              <span>Crie um alerta para começar o monitoramento.</span>
              <button type="button" onClick={() => openForm(activeSource === 'all' ? 'previsao' : activeSource)}>Criar alerta</button>
            </div>
          ) : (
            <div className="unified-alerts-list">
              {visibleAlerts.map(alert => {
                const source = normalizeSource(alert);
                const meta = sourceMeta(source);
                const Icon = meta.icon;
                return (
                  <article key={alert.id} className={`unified-alert-rule is-${source}`}>
                    <span className="unified-alert-rule-icon"><Icon /></span>
                    <div className="unified-alert-rule-content">
                      <div className="unified-alert-rule-top"><span>{meta.label}</span><em>Regra ativa</em></div>
                      <h3>{alertVariableLabel(alert)} {conditionLabel(alert.condicao)} {alert.valor}</h3>
                      <p>{source === 'sensor' ? `Sensor: ${alert.municipio || alert.device_id}` : `Município: ${alert.municipio}`}</p>
                      <small>Criado em {alert.criado_em ? new Date(alert.criado_em).toLocaleString('pt-BR') : 'data não informada'}</small>
                    </div>
                    <div className="unified-alert-rule-actions">
                      <button type="button" onClick={() => onNavigate(meta.target)}>Abrir origem</button>
                      <button type="button" className="is-delete" onClick={() => removeAlert(alert)} aria-label="Remover regra"><FaTrash /></button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="unified-alerts-explanation">
          <div><FaCloudSun /><strong>Alertas de previsão</strong><span>Monitoram condições futuras de um município, como chuva, temperatura, umidade e vento.</span></div>
          <div><FaMicrochip /><strong>Alertas de sensores</strong><span>Monitoram leituras reais dos equipamentos instalados em campo.</span></div>
          <div><FaBell /><strong>Ocorrências disparadas</strong><span>Quando uma regra é atingida, o aviso continua sendo entregue em Notificações, separado da configuração da regra.</span></div>
        </section>
      </main>

      {showForm && (
        <div className="unified-alerts-modal-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && setShowForm(false)}>
          <section className="unified-alerts-modal" role="dialog" aria-modal="true" aria-labelledby="unified-alert-form-title">
            <header>
              <div><span>Nova regra</span><h2 id="unified-alert-form-title">Criar alerta na Central</h2></div>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Fechar"><FaTimes /></button>
            </header>

            <form onSubmit={saveAlert}>
              <div className="unified-alerts-form-source">
                <button type="button" className={formSource === 'previsao' ? 'is-active' : ''} onClick={() => { setFormSource('previsao'); resetForm(); }}><FaCloudSun /><strong>Previsão</strong><span>Condições futuras por município</span></button>
                <button type="button" className={formSource === 'sensor' ? 'is-active' : ''} onClick={() => { setFormSource('sensor'); resetForm(); }}><FaMicrochip /><strong>Sensor</strong><span>Leituras reais do campo</span></button>
              </div>

              {formSource === 'previsao' ? (
                <>
                  <label className="unified-alerts-municipio">
                    <span>Município</span>
                    <div><FaSearch /><input value={municipio || searchMunicipio} placeholder="Digite para buscar..." onFocus={() => searchMunicipio.length >= 2 && setShowMunicipioDropdown(true)} onChange={event => { setMunicipio(event.target.value); setSearchMunicipio(event.target.value); setShowMunicipioDropdown(event.target.value.length >= 2); }} /></div>
                    {showMunicipioDropdown && searchMunicipio.length >= 2 && (
                      <div className="unified-alerts-suggestions">
                        {loadingMunicipios ? <span>Buscando...</span> : municipios.length ? municipios.map(item => (
                          <button type="button" key={`${item.name_muni}-${item.code_state}`} onClick={() => { setMunicipio(item.name_muni); setSearchMunicipio(item.name_muni); setShowMunicipioDropdown(false); }}>{item.name_muni}</button>
                        )) : <span>Nenhum município encontrado</span>}
                      </div>
                    )}
                  </label>
                  <div className="unified-alerts-form-grid">
                    <label><span>Variável</span><select value={variable} onChange={event => setVariable(event.target.value)}><option value="">Selecione...</option>{variaveisDisponiveis.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
                    <label><span>Condição</span><select value={condition} onChange={event => setCondition(event.target.value)}><option value="">Selecione...</option><option value="maior que">Maior que</option><option value="menor que">Menor que</option><option value="igual a">Igual a</option></select></label>
                  </div>
                  <label><span>Valor de referência</span><input type="number" step="0.1" value={value} onChange={event => setValue(event.target.value)} placeholder="Ex.: 25" /></label>
                </>
              ) : (
                <>
                  <label><span>Sensor</span><select value={selectedSensorId} onChange={event => { setSelectedSensorId(event.target.value); setSelectedTemplateId(''); }}><option value="">Selecione um sensor...</option>{sensors.map(sensor => <option key={sensor.id} value={sensor.id}>{sensor.nome_sensor || sensor.device_id}</option>)}</select></label>
                  {selectedSensor && <div className="unified-alerts-templates">{templates.map(template => <button type="button" key={template.id} className={selectedTemplateId === template.id ? 'is-active' : ''} onClick={() => setSelectedTemplateId(template.id)}><strong>{template.label}</strong><span>{template.description}</span></button>)}</div>}
                  {!sensors.length && <div className="unified-alerts-no-sensors"><FaMicrochip /><span>Nenhum sensor cadastrado nesta conta.</span><button type="button" onClick={() => onNavigate('sensores')}>Cadastrar sensor</button></div>}
                </>
              )}

              <footer><button type="button" onClick={() => setShowForm(false)}>Cancelar</button><button type="submit" className="is-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar alerta'}</button></footer>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
